import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { BottomBarSpacer, Container, Overline, Skeleton } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { PlacementBoard } from '@/features/placement/PlacementBoard'
import type { Sellable } from '@/features/pos/types'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'
import { NoAccess, StaffGate, isForbidden } from '@/lib/staffGate'
import { fetchMe, type StaffSession } from '@/lib/staffSession'

import {
  useCancelOrder,
  useMyOrders,
  useMyService,
  useServiceCatalogue,
  useServiceQuote,
  useSettle,
  useTakeOrder,
  type CartLine,
  type TakeOrderPayload,
} from '../api'
import { ChangeBanner, OrderCard, useErrorText, useWhere } from '../components/OrderCard'
import type { FloorOrder, FloorTable, SettleResult } from '../types'

type Tab = 'new' | 'collect' | 'progress' | 'done'
type Area = 'service' | 'placement'

/** Les droits qui ouvrent chaque partie de l'écran — ceux que le serveur exige. */
const SERVE = 'service.order.read'
const PLACE = 'tables.placement.read'
const PLACE_UPDATE = 'tables.placement.update'

/**
 * L'écran « Salle » — le service et le placement, sur un seul téléphone.
 *
 * L'hôtesse qui installe les groupes est souvent celle qui sert ensuite :
 * changer d'écran (et de connexion) à chaque geste lui ferait perdre le fil.
 * Les parties s'affichent donc **selon les droits** du compte connecté :
 *
 * * **Service** (`service.order.read`) — quatre onglets, dans l'ordre où la
 *   soirée les demande : prendre une commande, à encaisser, en cours,
 *   terminées. Ce que j'ai encaissé en espèces est compté à mon nom (« en
 *   poche ») jusqu'à ce que je le remette à la caisse d'un poste ;
 * * **Placement** (`tables.placement.read`) — les groupes qui ont réservé une
 *   table : arrivés à installer, attendus, installés.
 *
 * Une hôtesse seule ne voit que le placement, un serveur seul que le service,
 * quelqu'un qui a les deux bascule de l'un à l'autre d'un toucher.
 */
export function ServicePage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('service.floorTitle')}>
      {(session, signOut) => <FloorSpace session={session} onSignOut={signOut} />}
    </StaffGate>
  )
}

function FloorSpace({ session, onSignOut }: { session: StaffSession; onSignOut: () => void }) {
  const { t } = useTranslation()
  const eventQuery = usePublicEvent()
  const eventId = eventQuery.data?.id
  const [params, setParams] = useSearchParams()
  // Les mêmes droits, sous la même clé, que le conducteur et le back-office.
  const me = useQuery({
    queryKey: ['admin-me', eventId],
    queryFn: () => fetchMe(eventId),
    enabled: Boolean(eventId),
    retry: false,
  })

  useEffect(() => {
    document.title = t('service.floorTitle')
  }, [t])

  if (eventQuery.isPending || me.isPending) {
    return (
      <Container className="py-10">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
      </Container>
    )
  }

  const granted = new Set(me.data?.permissions ?? [])
  const canServe = granted.has(SERVE)
  const canPlace = granted.has(PLACE)
  if (!canServe && !canPlace) {
    return <NoAccess title={t('service.floorTitle')} onSignOut={onSignOut} />
  }

  // La régie envoie l'hôtesse ici avec `?tab=placement`.
  const wanted: Area = params.get('tab') === 'placement' ? 'placement' : 'service'
  const area: Area = !canServe ? 'placement' : !canPlace ? 'service' : wanted
  const switchTo = (next: Area) => {
    const updated = new URLSearchParams(params)
    if (next === 'placement') updated.set('tab', 'placement')
    else updated.delete('tab')
    setParams(updated, { replace: true })
  }

  const areaSwitch =
    canServe && canPlace ? (
      <nav className="mt-5 grid grid-cols-2 gap-1 rounded-full border border-line-subtle p-1">
        {(
          [
            ['service', t('service.areas.service')],
            ['placement', t('service.areas.placement')],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => switchTo(key)}
            aria-pressed={area === key}
            className={cn(
              'min-h-11 min-w-0 rounded-full px-2 text-sm',
              area === key ? 'bg-champagne-400/20 text-content-primary' : 'text-content-secondary',
            )}
          >
            {label}
          </button>
        ))}
      </nav>
    ) : null

  if (area === 'service') {
    return <ServiceSpace eventId={eventId} onSignOut={onSignOut} areaSwitch={areaSwitch} />
  }

  return (
    <Container className="max-w-3xl py-6 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* La langue à côté du nom de l'écran : le coin « compte » garde
              sa place, et rien ne pousse « Se déconnecter » hors de l'écran. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Overline>{t('service.floorTitle')}</Overline>
            <LanguageSwitcher />
          </div>
          <p className="mt-1 break-words font-display text-heading-md text-content-primary">
            {session.user.full_name || session.user.email}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          {t('checkin.logout')}
        </Button>
      </div>
      {areaSwitch}
      <PlacementBoard eventId={eventId} canUpdate={granted.has(PLACE_UPDATE)} variant="staff" />
    </Container>
  )
}

function ServiceSpace({
  eventId,
  onSignOut,
  areaSwitch,
}: {
  eventId: string | undefined
  onSignOut: () => void
  areaSwitch: ReactNode
}) {
  const { t, i18n } = useTranslation()
  const errorText = useErrorText()

  const me = useMyService(eventId, true)
  const [tab, setTab] = useState<Tab>('new')
  const active = useMyOrders(eventId, 'active', true)
  const done = useMyOrders(eventId, 'done', tab === 'done')
  const settle = useSettle(eventId, null)
  const cancel = useCancelOrder(eventId, null)
  const [lastSettle, setLastSettle] = useState<SettleResult | null>(null)
  const where = useWhere()

  const currency = me.data?.currency ?? 'EUR'
  const money = (minor: number) => formatMoney({ amount_minor: minor, currency }, i18n.language)

  if (me.isPending) {
    return (
      <Container className="py-10">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
      </Container>
    )
  }

  if (isForbidden(me.error)) {
    return <NoAccess title={t('service.floorTitle')} onSignOut={onSignOut} />
  }

  if (me.isError) {
    return (
      <Container className="max-w-md py-16">
        <p className="text-content-secondary">{errorText(me.error)}</p>
        <Button className="mt-6" variant="outline" onClick={onSignOut}>
          {t('checkin.logout')}
        </Button>
      </Container>
    )
  }

  const service = me.data
  const orders = active.data ?? []
  const toCollect = orders.filter((order) => order.status === 'pending_payment')
  const inProgress = orders.filter((order) => order.status !== 'pending_payment')
  const shown: FloorOrder[] =
    tab === 'collect' ? toCollect : tab === 'progress' ? inProgress : (done.data ?? [])
  const actionError = errorText(settle.error ?? cancel.error)

  return (
    <Container className="max-w-3xl py-6 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* La langue à côté du nom de l'écran : le coin « compte » garde
              sa place, et rien ne pousse « Se déconnecter » hors de l'écran. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Overline>{t('service.floorTitle')}</Overline>
            <LanguageSwitcher />
          </div>
          <p className="mt-1 break-words font-display text-heading-md text-content-primary">
            {service?.name}
          </p>
        </div>
        <div className="text-right">
          <p className="text-caption text-content-muted">{t('service.inPocketLabel')}</p>
          <p
            className={cn(
              'font-display text-heading-md tabular-nums',
              (service?.balance.held_minor ?? 0) > 0 ? 'text-champagne-300' : 'text-content-primary',
            )}
          >
            {money(service?.balance.held_minor ?? 0)}
          </p>
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            {t('checkin.logout')}
          </Button>
        </div>
      </div>
      {(service?.balance.held_minor ?? 0) > 0 ? (
        <p className="mt-2 text-caption text-content-muted">{t('service.handOverHint')}</p>
      ) : null}

      {areaSwitch}

      {/* Quatre onglets ne tiennent pas sur 375 px sans couper les mots
          (« Aufnehmen », « Zu kassieren ») : deux rangées sur téléphone. */}
      <nav className="mt-5 grid grid-cols-2 gap-1 rounded-3xl border border-line-subtle p-1 sm:grid-cols-4 sm:rounded-full">
        {(
          [
            ['new', t('service.tabs.new'), null],
            ['collect', t('service.tabs.collect'), toCollect.length],
            ['progress', t('service.tabs.progress'), inProgress.length],
            ['done', t('service.tabs.done'), null],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'flex min-h-11 min-w-0 items-center justify-center gap-1 rounded-full px-2 text-center text-sm leading-tight',
              tab === key ? 'bg-ember text-white' : 'text-content-secondary',
            )}
          >
            <span className="min-w-0">{label}</span>
            {count ? (
              <span className="rounded-full bg-black/30 px-1.5 font-mono text-caption">{count}</span>
            ) : null}
          </button>
        ))}
      </nav>

      {lastSettle ? (
        <div className="mt-4">
          <ChangeBanner
            orderNumber={lastSettle.order.order_number}
            table={where(lastSettle.order).title}
            stations={lastSettle.order.stations}
            change={lastSettle.change_minor}
            register={lastSettle.register_label}
            money={money}
            onDismiss={() => setLastSettle(null)}
          />
        </div>
      ) : null}
      {actionError ? <p className="mt-4 text-sm text-state-danger">{actionError}</p> : null}

      {tab === 'new' && service ? (
        <NewOrder
          eventId={eventId}
          myTables={service.my_tables}
          tables={service.tables}
          money={money}
          onPlaced={() => setTab('collect')}
        />
      ) : (
        <>
          {(tab === 'done' ? done.isPending : active.isPending) ? (
            <Skeleton className="mt-6 h-40 w-full" />
          ) : shown.length === 0 ? (
            <p className="mt-10 text-center text-content-muted">{t(`service.empty.${tab}`)}</p>
          ) : (
            <ul className="mt-5 grid gap-3">
              {shown.map((order) => (
                <OrderCard
                  key={order.order_id}
                  order={order}
                  station={null}
                  money={money}
                  busy={settle.isPending || cancel.isPending}
                  onSettle={
                    order.status === 'pending_payment'
                      ? (payload) =>
                          settle.mutate(
                            { orderId: order.order_id, ...payload },
                            { onSuccess: (result) => setLastSettle(result) },
                          )
                      : undefined
                  }
                  onCancel={
                    tab === 'done'
                      ? undefined
                      : (reason) => cancel.mutate({ orderId: order.order_id, reason })
                  }
                />
              ))}
            </ul>
          )}
        </>
      )}
    </Container>
  )
}

/**
 * Prendre une commande : la table, puis les articles, puis l'envoi.
 *
 * Le total affiché est **celui du serveur** : chaque toucher renvoie le panier
 * à chiffrer. Rien n'est réservé tant qu'on n'a pas envoyé.
 */
function NewOrder({
  eventId,
  myTables,
  tables,
  money,
  onPlaced,
}: {
  eventId: string | undefined
  myTables: FloorTable[]
  tables: FloorTable[]
  money: (minor: number) => string
  onPlaced: (order: FloorOrder) => void
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const catalogue = useServiceCatalogue(eventId, true)
  const take = useTakeOrder(eventId)

  const [tableId, setTableId] = useState<string | null>(null)
  // « Sans table » : un client debout, au bar, près de la piste. Son prénom
  // remplace le numéro de table — c'est par lui qu'on le retrouve.
  const [noTable, setNoTable] = useState(false)
  const [customer, setCustomer] = useState('')
  const [spot, setSpot] = useState('')
  const [fulfilment, setFulfilment] = useState<'deliver' | 'pickup'>('deliver')
  const [showAll, setShowAll] = useState(myTables.length === 0)
  const [cart, setCart] = useState<Record<string, number>>({})
  const [method, setMethod] = useState<'cash' | 'paypal'>('cash')
  const [note, setNote] = useState('')
  // La barre du panier grandit avec lui : la page lui réserve sa hauteur.
  const barRef = useRef<HTMLDivElement>(null)

  const items = catalogue.data?.items ?? []
  const lines: CartLine[] = useMemo(
    () =>
      Object.entries(cart)
        .filter(([, quantity]) => quantity > 0)
        .map(([key, quantity]) => {
          const [kind, ref_id] = key.split(':')
          return { kind: kind as CartLine['kind'], ref_id: ref_id ?? '', quantity }
        }),
    [cart],
  )
  const quote = useServiceQuote(eventId, lines)
  const table = tables.find((row) => row.id === tableId) ?? null

  const groups: { id: string; title: string; entries: Sellable[] }[] = [
    {
      id: 'kitchen',
      title: t('service.stationShort.kitchen'),
      entries: items.filter((row) => row.station === 'kitchen'),
    },
    {
      id: 'bar',
      title: t('service.stationShort.bar'),
      entries: items.filter((row) => row.station !== 'kitchen'),
    },
  ].filter((group) => group.entries.length > 0)

  function add(key: string, delta: number) {
    setCart((current) => {
      const next = { ...current }
      const value = (next[key] ?? 0) + delta
      if (value <= 0) delete next[key]
      else next[key] = Math.min(value, 50)
      return next
    })
  }

  const choices = showAll ? tables : myTables
  const name = customer.trim()
  // Prête à partir : une table, ou — sans table — au moins un prénom.
  const ready = noTable ? name.length > 0 : table !== null
  const destination = noTable
    ? [t('service.where.noTable'), name].filter(Boolean).join(' · ')
    : table
      ? table.label
      : t('service.pickTable')

  return (
    <div className="mt-5 space-y-6">
      <section>
        <p className="overline text-content-muted">{t('service.stepTable')}</p>
        {/* Sans table, la grille des tables s'efface : sur un téléphone, le
            prénom à saisir se retrouvait sinon sous vingt tables, hors de
            l'écran — le serveur ne voyait pas qu'on attendait quelque chose. */}
        {noTable ? null : (
          <>
            <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {/* « Sans table » en premier : c'est le seul choix qui ne dépend
                  pas du plan de salle, il ne doit pas se perdre au bout de la
                  liste. */}
              <button
                type="button"
                onClick={() => {
                  setNoTable(true)
                  setTableId(null)
                }}
                className="min-h-16 rounded-lg border border-dashed border-line bg-bg-elevated px-2 py-2 text-left"
              >
                <span className="block font-display text-heading-sm leading-tight text-content-primary">
                  {t('service.where.noTable')}
                </span>
                <span className="line-clamp-2 block text-xs leading-tight text-content-muted">
                  {t('service.noTableTile')}
                </span>
              </button>
              {choices.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setTableId(row.id)}
                  className={cn(
                    'min-h-16 rounded-lg border px-2 py-2 text-left',
                    tableId === row.id
                      ? 'border-ember-400 bg-ember-500/15'
                      : 'border-line bg-bg-elevated',
                  )}
                >
                  <span className="block font-display text-heading-sm text-content-primary">
                    {row.table_number}
                  </span>
                  <span className="block truncate text-caption text-content-muted">
                    {row.name ?? ''}
                  </span>
                  {/* Toutes les tables : on voit à qui chacune revient. */}
                  {showAll ? (
                    <span className="line-clamp-2 block text-xs leading-tight text-champagne-400">
                      {row.waiter_name ?? t('service.noWaiter')}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            {choices.length === 0 ? (
              <p className="mt-2 text-sm text-content-muted">{t('service.noTables')}</p>
            ) : null}
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setShowAll(!showAll)}>
              {showAll ? t('service.onlyMine') : t('service.allTables')}
            </Button>
            {table && table.waiter_id && !myTables.some((row) => row.id === table.id) ? (
              <p className="text-caption text-content-muted">
                {t('service.otherWaiter', { name: table.waiter_name })}
              </p>
            ) : null}
          </>
        )}

        {noTable ? (
          <div className="mt-2 space-y-3 rounded-lg border border-dashed border-ember-400 bg-ember-500/10 p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-display text-heading-sm text-content-primary">
                {t('service.where.noTable')}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setNoTable(false)}>
                {t('service.pickTable')}
              </Button>
            </div>
            <p className="text-sm text-content-secondary">{t('service.noTableHint')}</p>
            <label className="block text-caption text-content-muted">
              {t('service.customerName')}
              <input
                value={customer}
                maxLength={60}
                autoFocus
                autoComplete="off"
                autoCapitalize="words"
                onChange={(event) => setCustomer(event.target.value)}
                className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
              />
            </label>
            <label className="block text-caption text-content-muted">
              {t('service.spotNote')}
              <input
                value={spot}
                maxLength={60}
                autoComplete="off"
                placeholder={t('service.spotPlaceholder')}
                onChange={(event) => setSpot(event.target.value)}
                className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
              />
            </label>
            <div>
              <p className="text-caption text-content-muted">{t('service.fulfilmentLabel')}</p>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {(['deliver', 'pickup'] as const).map((option) => (
                  <Button
                    key={option}
                    size="sm"
                    variant={fulfilment === option ? 'ember' : 'outline'}
                    aria-pressed={fulfilment === option}
                    className="h-auto min-h-11 whitespace-normal py-2 leading-tight"
                    onClick={() => setFulfilment(option)}
                  >
                    {t(`service.fulfilment.${option}`)}
                  </Button>
                ))}
              </div>
              <p className="mt-1 text-caption text-content-muted">
                {t(`service.fulfilmentHint.${fulfilment}`)}
              </p>
            </div>
          </div>
        ) : null}
      </section>

      <section>
        <p className="overline text-content-muted">{t('service.stepItems')}</p>
        {catalogue.isPending ? <Skeleton className="mt-3 h-40 w-full" /> : null}
        {groups.map((group) => (
          <div key={group.id} className="mt-3">
            <p className="text-sm text-champagne-300">{group.title}</p>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {group.entries.map((product) => {
                const key = `${product.kind}:${product.ref_id}`
                const inCart = cart[key] ?? 0
                const soldOut = product.remaining === 0
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={soldOut}
                    onClick={() => add(key, 1)}
                    className={cn(
                      'relative flex min-h-20 flex-col justify-between rounded-lg border p-3 text-left active:scale-[.98]',
                      soldOut ? 'border-line-subtle opacity-40' : 'border-line bg-bg-elevated',
                      inCart > 0 && 'border-champagne-400',
                    )}
                  >
                    <span className="pr-6 text-sm leading-snug text-content-primary">
                      {product.label}
                    </span>
                    <span className="mt-1 font-display text-heading-sm tabular-nums text-champagne-300">
                      {money(product.price_minor)}
                    </span>
                    {inCart > 0 ? (
                      <span className="absolute right-2 top-2 flex h-6 min-w-6 items-center justify-center rounded-full bg-ember-500 px-1.5 font-mono text-caption text-white">
                        {inCart}
                      </span>
                    ) : null}
                    {soldOut ? (
                      <span className="absolute right-2 top-2 text-caption text-content-muted">
                        {t('pos.soldOut')}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </section>

      <section>
        <p className="overline text-content-muted">{t('service.stepNote')}</p>
        <input
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('onsite.notePlaceholder')}
          className="mt-2 h-12 w-full rounded-md border border-line bg-bg-elevated px-3 text-content-primary"
        />
      </section>

      {/* Le panier, collé en bas : la table et le total ne sortent jamais
          de l'écran. */}
      <BottomBarSpacer barRef={barRef} />
      <div
        ref={barRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-bg-elevated pb-[env(safe-area-inset-bottom)]"
      >
        <div className="mx-auto max-w-3xl px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-sm text-content-secondary">{destination}</span>
            <span className="font-display text-heading-lg tabular-nums text-content-primary">
              {lines.length === 0 ? money(0) : quote.data ? money(quote.data.total_minor) : '…'}
            </span>
          </div>
          {lines.length > 0 ? (
            <>
              <ul className="mt-2 max-h-32 overflow-y-auto">
                {(quote.data?.lines ?? []).map((line) => {
                  const key = `${line.kind}:${line.ref_id}`
                  return (
                    <li key={key} className="flex items-center justify-between gap-2 py-0.5">
                      <span className="min-w-0 truncate text-sm text-content-secondary">
                        {line.quantity} × {line.label}
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        <span className="mr-1 text-caption tabular-nums text-content-muted">
                          {money(line.line_total_minor)}
                        </span>
                        <button
                          type="button"
                          aria-label={`− ${line.label}`}
                          onClick={() => add(key, -1)}
                          className="h-10 w-10 rounded-full border border-line text-content-primary"
                        >
                          −
                        </button>
                        <button
                          type="button"
                          aria-label={`+ ${line.label}`}
                          onClick={() => add(key, 1)}
                          className="h-10 w-10 rounded-full border border-line text-content-primary"
                        >
                          +
                        </button>
                      </span>
                    </li>
                  )
                })}
              </ul>
              <p className="mt-1 text-caption text-content-muted">{t('service.serverTotal')}</p>
              {quote.isError ? (
                <p className="text-sm text-state-danger">{errorText(quote.error)}</p>
              ) : null}
              <div className="mt-2 flex gap-2">
                {(['cash', 'paypal'] as const).map((option) => (
                  <Button
                    key={option}
                    size="sm"
                    variant={method === option ? 'ember' : 'outline'}
                    className="flex-1"
                    onClick={() => setMethod(option)}
                  >
                    {t(`service.methods.${option}`)}
                  </Button>
                ))}
              </div>
              {/* Le bouton grisé sans dire pourquoi laisserait le serveur
                  chercher : sans table, c'est le prénom qui manque. */}
              {noTable && !name ? (
                <p className="mt-2 text-sm text-champagne-300">{t('service.nameRequired')}</p>
              ) : null}
              {take.isError ? (
                <p className="mt-2 text-sm text-state-danger">{errorText(take.error)}</p>
              ) : null}
              <div className="mt-2 flex gap-2">
                <Button
                  size="lg"
                  className="flex-1"
                  disabled={!ready || take.isPending || quote.isError}
                  onClick={() => {
                    if (!ready) return
                    const common = { lines, payment_method: method, note: note.trim() || null }
                    const payload: TakeOrderPayload = noTable
                      ? {
                          ...common,
                          table_id: null,
                          customer_label: name,
                          spot_note: spot.trim() || null,
                          fulfilment,
                        }
                      : { ...common, table_id: table?.id ?? '' }
                    take.mutate(payload, {
                      onSuccess: (order) => {
                        setCart({})
                        setNote('')
                        // Le client suivant n'est pas le même : on ne garde
                        // ni son prénom ni son repère.
                        setCustomer('')
                        setSpot('')
                        onPlaced(order)
                      },
                    })
                  }}
                >
                  {take.isPending ? t('state.loading') : t('service.send')}
                </Button>
                <Button variant="ghost" size="lg" onClick={() => setCart({})}>
                  {t('pos.clear')}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
